<template>
  <main class="biotron-check mx-auto" aria-labelledby="biotron-page-title">
    <h1 id="biotron-page-title" class="text-center">Check your Biotron</h1>
    <section class="check-intro border rounded-3 p-3 p-md-4 mb-4">
      <p class="small text-uppercase text-muted mb-1">Step-by-step test</p>
      <h2 class="h4">Find where a problem starts</h2>
      <p class="mb-2">First the page checks the USB connection without changing anything. Then it asks you to try the plant, light sensor and controls.</p>
      <p class="mb-0 fw-semibold">Step {{ diagnosticStage }} of 4 · {{ diagnosticStageLabel }}</p>
    </section>

    <DeviceSelector ref="biotronSelector" regex-name="Biotron" @device_changed="deviceChanged"
                    @firmware_version_changed="firmwareVersionChanged"
                    @settings_readback="settingsReadbackReceived"
                    @midi_activity="midiActivityReceived"
                    text_label="Biotron USB connection" check-versions-flag class="mb-4"/>

    <p v-if="firmwareVersion && firmwareVersion !== reviewedFirmware" class="alert alert-warning" role="status">
      This test has only been reviewed with firmware {{ reviewedFirmware }}. Your Biotron has {{ firmwareVersion }}, so the result would not be reliable.
      Nothing will be updated automatically. <a href="https://help.playtronica.com/troubleshooting/firmware-reset/#biotron-recovery" target="_blank" rel="noopener">See the firmware instructions</a>.
    </p>

    <BiotronSafeInspector :device-connected="Boolean(device)"
                          :firmware-version="firmwareVersion || ''"
                          :settings-readback-loaded="Boolean(settingsReadback)"
                          :device-epoch="deviceEpoch"
                          :mutation-epoch="deviceMutationEpoch"
                          :inspection-result="safeInspectionResult"
                          :build-id="buildId"
                          :standalone-download="false"
                          @inspection_requested="runSafeInspection"
                          @inspection_state_changed="safeInspectionRunning = $event"
                          @inspection_summary_changed="safeInspectorSummary = $event"
                          @inspector_report_changed="safeDiagnosticsReport = $event"/>

    <BiotronManualDiagnostics v-if="safeInspectorSummary === 'pass'"
                              :device-connected="Boolean(device)"
                              :firmware-version="firmwareVersion || ''"
                              :inspector-summary="safeInspectorSummary"
                              :device-epoch="deviceEpoch"
                              :mutation-epoch="deviceMutationEpoch"
                              :midi-event="lastMidiEvent"
                              :plant-channel="plantChannel"
                              :light-channel="lightChannel"
                              :light-muted="lightMuted"
                              :standalone-download="false"
                              @capture_state_changed="manualDiagnosticsRunning = $event"
                              @manual_summary_changed="manualDiagnosticsSummary = $event"
                              @manual_report_changed="manualDiagnosticsReport = $event"/>

    <section v-if="manualDiagnosticsSummary === 'pass'" class="external-checks border rounded-3 p-3 p-md-4 mb-4">
      <h2 class="h4">Tests to do outside this page</h2>
      <p>A browser cannot prove these two results by itself, so they stay clearly marked as not tested.</p>
      <ul class="list-group mb-3">
        <li class="list-group-item d-flex justify-content-between gap-3"><span>Another MIDI app can open Biotron after Settings releases it</span><span class="badge text-bg-secondary">Not tested</span></li>
        <li class="list-group-item d-flex justify-content-between gap-3"><span>Installed Settings opens after the internet is switched off and the browser is restarted</span><span class="badge text-bg-secondary">Not tested</span></li>
      </ul>
      <p class="mb-0"><a href="https://help.playtronica.com/software/biotron-offline-settings/" target="_blank" rel="noopener">See how to test another MIDI app and offline use</a>.</p>
    </section>

    <div class="d-flex flex-column flex-sm-row gap-2 mb-4">
      <button type="button" class="btn btn-primary" :disabled="!safeDiagnosticsReport" @click="downloadCombinedDiagnosticsReport">
        Download one report for support
      </button>
      <router-link class="btn btn-outline-primary" to="/biotron">Back to settings</router-link>
    </div>
  </main>
</template>

<script>
import {saveAs} from "@progress/kendo-file-saver"
import DeviceSelector from "@biotron-device-selector"
import BiotronSafeInspector from "@biotron-safe-inspector"
import BiotronManualDiagnostics from "@biotron-manual-diagnostics"
import {
  BIOTRON_REVIEWED_FIRMWARE_VERSION,
  sha256Hex
} from "@/assets/js/BiotronSafeInspector"
import {
  BIOTRON_SETTINGS_QUERY_ID,
  biotronCommandsFromSettingsReply
} from "@biotron-settings-readback"

const delay = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))

export default {
  name: "BiotronDiagnosticsPage",
  components: {DeviceSelector, BiotronSafeInspector, BiotronManualDiagnostics},
  data() {
    return {
      reviewedFirmware: BIOTRON_REVIEWED_FIRMWARE_VERSION,
      device: null,
      deviceEpoch: 0,
      deviceMutationEpoch: 0,
      firmwareVersion: null,
      settingsReadback: null,
      settingsReadbackRequestId: 0,
      settingsValues: null,
      safeInspectionRunning: false,
      safeInspectionResult: null,
      safeInspectorSummary: "incomplete",
      safeDiagnosticsReport: null,
      manualDiagnosticsRunning: false,
      manualDiagnosticsSummary: "incomplete",
      manualDiagnosticsReport: null,
      lastMidiEvent: null,
      buildId: process.env.VUE_APP_BUILD_ID || "local-build"
    }
  },
  computed: {
    diagnosticStage() {
      if (!this.device) return 1
      if (this.safeInspectorSummary !== "pass") return 2
      if (this.manualDiagnosticsSummary !== "pass") return 3
      return 4
    },
    diagnosticStageLabel() {
      const labels = ["", "Connect Biotron", "Check the USB connection", "Check the plant, light and controls", "Test another MIDI app and offline use"]
      return labels[this.diagnosticStage]
    },
    plantChannel() {
      return Number(this.settingsValues && this.settingsValues.plant_midi_channel) || 1
    },
    lightChannel() {
      return Number(this.settingsValues && this.settingsValues.light_midi_channel) || 2
    },
    lightMuted() {
      return Boolean(this.settingsValues && this.settingsValues.light_no_velocity)
    }
  },
  methods: {
    deviceChanged(device) {
      this.deviceEpoch++
      this.deviceMutationEpoch++
      this.device = device || null
      this.firmwareVersion = null
      this.settingsReadback = null
      this.settingsValues = null
      this.safeInspectionResult = null
      this.safeInspectorSummary = "incomplete"
      this.safeDiagnosticsReport = null
      this.manualDiagnosticsSummary = "incomplete"
      this.manualDiagnosticsReport = null
      this.lastMidiEvent = null
      if (device) this.loadFreshSettings(device, this.deviceEpoch)
    },
    firmwareVersionChanged(version) {
      this.firmwareVersion = version || null
    },
    settingsReadbackReceived(reply) {
      if (!this.device) return
      this.settingsReadback = reply
      const values = biotronCommandsFromSettingsReply(reply)
      if (values) this.settingsValues = values
    },
    midiActivityReceived(event) {
      this.lastMidiEvent = event
    },
    async loadFreshSettings(device, epoch) {
      for (let attempt = 0; attempt < 3; attempt++) {
        if (this.device !== device || this.deviceEpoch !== epoch) return
        const requestId = ++this.settingsReadbackRequestId & 0x7f
        device.send([0xf0, 0x14, 0x0d, BIOTRON_SETTINGS_QUERY_ID, 1, requestId, 0xf7])
        for (let poll = 0; poll < 6; poll++) {
          await delay(100)
          if (this.device !== device || this.deviceEpoch !== epoch) return
          if (this.settingsReadback && this.settingsReadback.requestId === requestId) return
        }
      }
    },
    async runSafeInspection(request) {
      // The child emits `inspection_state_changed(true)` immediately before
      // `inspection_requested`. Do not treat that expected UI state as a
      // duplicate transport call; the child already disables its start button.
      if (!this.device) return
      this.safeInspectionRunning = true
      this.safeInspectionResult = null
      try {
        const selector = this.$refs.biotronSelector
        this.safeInspectionResult = selector && typeof selector.inspectAllPortsReadOnly === "function"
          ? await selector.inspectAllPortsReadOnly(request)
          : {...request, status: "blocked", reason: "inspector_transport_unavailable", ports: []}
      } catch (error) {
        this.safeInspectionResult = {...request, status: "blocked", reason: "unexpected_inspector_error", ports: []}
        console.log("Biotron diagnostic failed", error)
      } finally {
        this.safeInspectionRunning = false
      }
    },
    async downloadCombinedDiagnosticsReport() {
      if (!this.safeDiagnosticsReport) return
      const report = {
        schema_version: 1,
        created_at: new Date().toISOString(),
        product: "Biotron",
        settings_build: this.buildId,
        firmware_contract: BIOTRON_REVIEWED_FIRMWARE_VERSION,
        sections: {
          digital: this.safeDiagnosticsReport,
          physical: this.manualDiagnosticsReport || {summary: "not_tested"},
          external: {
            daw_handoff: {status: "not_tested", evidence_kind: "external"},
            offline_restart: {status: "not_tested", evidence_kind: "external"}
          }
        }
      }
      report.checksum = {
        algorithm: "SHA-256",
        scope: "canonical JSON before checksum field",
        value: await sha256Hex(JSON.stringify(report), crypto)
      }
      const file = new File([JSON.stringify(report, null, 2)],
        `biotron-support-report-${new Date().toISOString().slice(0, 10)}.json`,
        {type: "application/json"})
      saveAs(file, file.name)
    }
  }
}
</script>

<style scoped>
.biotron-check { max-width: 48rem; }
.check-intro { background: #eef4ff; }
.external-checks { background: #f8f9fa; }
@media (max-width: 575.98px) { .biotron-check { width: 100%; } }
</style>
